import React, { useState } from "react";
import { Wand2, Sparkles, AlertTriangle, Check, X, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "./ui/dialog";
import { aiApi, formatApiError } from "../lib/ledgerApi";
import { toast } from "sonner";

const fmt = (s) => {
  try {
    const n = Number(s);
    return Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 6 }) : s;
  } catch { return s; }
};

const SUGGESTIONS = [
  "send 100 USD to globex@neopay for invoice 2024-09",
  "pay initech 250 EUR tomorrow at 9am",
  "transfer 50 NEO to acme@neopay",
];

export default function NlpComposer({ onExecuted, onHeld, onScheduled }) {
  const [text, setText] = useState("");
  const [parsing, setParsing] = useState(false);
  const [draft, setDraft] = useState(null); // parsed result
  const [confirming, setConfirming] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  const onParse = async () => {
    if (!text.trim()) return;
    setParsing(true);
    try {
      const r = await aiApi.parseNlp(text.trim());
      setDraft(r);
      if (r.needs_clarification) {
        toast.warning("Clarification needed", { description: r.clarification_questions?.[0] });
      } else {
        setConfirming(true);
      }
    } catch (e) {
      toast.error("AI parse failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setParsing(false);
    }
  };

  const onConfirm = async () => {
    if (!draft?.draft_id) return;
    setBusy(true);
    try {
      const r = await aiApi.confirmNlp(draft.draft_id, confirmText || null);
      setConfirming(false);
      setDraft(null);
      setText("");
      setConfirmText("");
      if (r.scheduled) {
        toast.success("Scheduled", { description: `Fires ${new Date(r.execute_at).toLocaleString()}` });
        onScheduled?.(r);
      } else if (r.held) {
        toast.warning("Held for review", { description: `Risk ${r.verdict?.final_score}/100` });
        onHeld?.(r);
      } else if (r.executed) {
        toast.success("Sent", { description: r.transactions?.[0]?.tx_id?.slice(0, 16) + "…" });
        onExecuted?.(r);
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "LARGE_AMOUNT_REQUIRES_CONFIRMATION") {
        toast.warning("Large amount", { description: detail.message });
      } else if (detail && typeof detail === "object" && detail.code === "BLOCKED_BY_RISK") {
        toast.error("Blocked by risk engine", { description: detail.message });
        setConfirming(false);
      } else {
        toast.error("Confirm failed", { description: formatApiError(detail) });
      }
    } finally {
      setBusy(false);
    }
  };

  const isLarge = draft && draft.amount && Number(draft.amount) >= 5000;
  const expectedPhrase = draft && isLarge
    ? `YES SEND ${draft.amount} ${draft.currency} TO ${draft.to_vpa?.toUpperCase()}`
    : null;

  return (
    <div className="border border-border rounded-sm bg-card p-5 space-y-4" data-testid="nlp-composer">
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-brand" strokeWidth={1.5} />
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Ask in plain English</div>
      </div>
      <div className="flex gap-2">
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="e.g. send 100 USD to globex@neopay for invoice 2024-09"
          data-testid="nlp-input"
          className="rounded-sm font-mono"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onParse();
            }
          }}
        />
        <Button onClick={onParse} disabled={parsing || !text.trim()} data-testid="nlp-parse" className="rounded-sm">
          {parsing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3 mr-1" strokeWidth={1.5} />}
          Parse
        </Button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {SUGGESTIONS.map((s) => (
          <button
            key={s}
            onClick={() => setText(s)}
            data-testid={`nlp-suggest-${s.slice(0, 6)}`}
            className="px-2 py-1 border border-border rounded-sm text-[11px] font-mono text-muted-foreground hover:border-foreground hover:text-foreground transition-colors"
          >
            {s}
          </button>
        ))}
      </div>

      {draft?.needs_clarification && (
        <div className="border-t border-border pt-3 text-[12px] space-y-1">
          <div className="text-amber-600 dark:text-amber-400 inline-flex items-center gap-1.5">
            <AlertTriangle className="w-3 h-3" strokeWidth={1.5} /> Need a bit more info
          </div>
          <ul className="font-mono text-muted-foreground space-y-0.5">
            {draft.clarification_questions?.map((q, i) => (
              <li key={i}>· {q}</li>
            ))}
          </ul>
          {draft.ai_offline && (
            <div className="text-[11px] text-muted-foreground italic">AI offline — used heuristic parser.</div>
          )}
        </div>
      )}

      <Dialog open={confirming} onOpenChange={(o) => { if (!o) setConfirming(false); }}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle>Confirm AI-drafted transfer</DialogTitle>
            <DialogDescription>
              The model parsed your request below. Review carefully — the ledger is irreversible.
            </DialogDescription>
          </DialogHeader>
          {draft && (
            <div className="space-y-2 font-mono text-xs">
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">To</span>
                <span className="col-span-2">{draft.recipient_legal_name} ({draft.to_vpa})</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">Amount</span>
                <span className="col-span-2 text-status-ok">{fmt(draft.amount)} {draft.currency}</span>
              </div>
              {draft.execute_at && (
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-muted-foreground">Schedule</span>
                  <span className="col-span-2">{new Date(draft.execute_at).toLocaleString()}</span>
                </div>
              )}
              {draft.memo && (
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-muted-foreground">Memo</span>
                  <span className="col-span-2">{draft.memo}</span>
                </div>
              )}
              {draft.ai_offline && (
                <div className="text-[11px] text-muted-foreground italic mt-2">
                  AI offline — heuristic-parsed.
                </div>
              )}
              {isLarge && (
                <div className="border-t border-border pt-3 mt-3 space-y-2">
                  <div className="text-amber-600 dark:text-amber-400 text-[11px]">
                    Amount ≥ $5,000 — type the phrase below to authorise.
                  </div>
                  <div className="font-mono text-[11px] bg-muted p-2 rounded-sm select-all">{expectedPhrase}</div>
                  <Input
                    data-testid="nlp-confirm-phrase"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="Type the phrase exactly…"
                    className="rounded-sm font-mono"
                  />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirming(false)} className="rounded-sm">
              <X className="w-3 h-3 mr-1" strokeWidth={1.5} /> Cancel
            </Button>
            <Button
              onClick={onConfirm}
              disabled={busy || (isLarge && confirmText !== expectedPhrase)}
              data-testid="nlp-confirm"
              className="rounded-sm"
            >
              <Check className="w-3 h-3 mr-1" strokeWidth={1.5} />
              {busy ? "Sending…" : draft?.execute_at ? "Schedule" : "Send now"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
